import React, { useState, useEffect, useCallback } from 'react';
import { chunkedUpload, getUploadStatus, logEvent } from '../services/api';
import { getSavedRecordings, deleteRecording, getUploadProgress, saveUploadProgress, deleteUploadProgress } from '../utils/idbStorage';
import LoadingSpinner from './LoadingSpinner';
import { useT } from '../utils/i18n';

const SavedRecordings = ({ videoId, narrator, refreshToken, onUploadSuccess }) => {
  const t = useT();
  const [savedRecordings, setSavedRecordings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const loadSavedRecordings = useCallback(async () => {
    setIsLoading(true);
    try {
      const recordings = await getSavedRecordings(videoId, narrator);

      const enriched = await Promise.all(
        recordings.map(async (rec) => {
          const progress = await getUploadProgress(rec.id);
          const uploadId = rec.uploadId || progress?.uploadId;
          let session = null;
          if (uploadId) {
            try {
              session = await getUploadStatus(uploadId);
            } catch (err) {
              // Session expired or was discarded; the local copy still stands.
              session = null;
            }
          }
          return { ...rec, uploadProgress: progress, uploadId, session };
        })
      );

      // A recording the server already published is not a pending recording:
      // keeping it invites the narrator to upload the same audio a second time.
      const published = enriched.filter(rec => rec.session?.status === 'complete');
      await Promise.all(published.map(async (rec) => {
        await deleteRecording(rec.id);
        await deleteUploadProgress(rec.id);
        console.log("Dropped cached recording already published by the server:", rec.id);
      }));

      setSavedRecordings(enriched.filter(rec => rec.session?.status !== 'complete'));
    } catch (err) {
      console.error("Failed to load saved recordings:", err);
      setError(t('Failed to load saved recordings. This may indicate storage permission issues.'));
    } finally {
      setIsLoading(false);
    }
  }, [videoId, narrator, t]);

  useEffect(() => {
    loadSavedRecordings();
  }, [loadSavedRecordings, refreshToken]);

  const handleLog = async (action, value = null) => {
    try {
      await logEvent({ video_id: videoId, action, value, narrator });
    } catch (err) {
      console.error("Failed to log event:", action, err);
    }
  };

  const handleRetryUpload = async (recording) => {
    setError(null);
    setUploadingId(recording.id);
    setUploadProgress(0);

    try {
      const response = await chunkedUpload(
        videoId,
        narrator,
        recording.blob,
        recording.filename,
        {
          resumeUploadId: recording.uploadId,
          // Re-cuts the recording exactly as it was streamed, so only the slices
          // the server is missing get sent.
          sliceSizes: recording.sliceSizes,
          onProgress: setUploadProgress,
          onChunkComplete: async ({ uploadId, completedChunks, totalChunks }) => {
            try {
              await saveUploadProgress({
                recordingId: recording.id,
                uploadId,
                videoId: parseInt(videoId, 10),
                narrator,
                completedChunks,
                totalChunks,
                timestamp: new Date().toISOString()
              });
            } catch (err) {
              console.warn("Failed to save upload progress:", err);
            }
          }
        }
      );

      console.log("Retry upload successful:", response);

      await deleteRecording(recording.id);
      await deleteUploadProgress(recording.id);
      setSavedRecordings(prev => prev.filter(rec => rec.id !== recording.id));

      onUploadSuccess(response);
    } catch (err) {
      console.error("Retry upload failed:", err.response?.data || err.message);
      setError(`อัพโหลดไม่สำเร็จ: ${err.response?.data?.detail || err.message}. สามารถลองอีกครั้งได้`);
      handleLog('upload_fail', err.response?.data?.detail || err.message);
      loadSavedRecordings();
    } finally {
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (recording) => {
    if (window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบไฟล์เสียงนี้?`)) {
      try {
        await deleteRecording(recording.id);
        await deleteUploadProgress(recording.id);
        setSavedRecordings(prev => prev.filter(rec => rec.id !== recording.id));
      } catch (err) {
        console.error("Failed to delete recording:", err);
        setError("ลบไฟล์เสียงไม่สำเร็จ");
      }
    }
  };

  if (isLoading && savedRecordings.length === 0) {
    return <div className="text-center mt-3"><LoadingSpinner small /> กำลังโหลดไฟล์เสียงที่บันทึกไว้...</div>;
  }

  if (savedRecordings.length === 0) {
    return null; // Don't show anything if no saved recordings
  }

  return (
    <div className="saved-recordings mt-4">
      <h5>ไฟล์เสียงที่บันทึกไว้ในเครื่อง</h5>
      <p className="text-muted small">ไฟล์เสียงเหล่านี้ถูกบันทึกไว้ในเครื่อง สามารถอัพโหลดได้อีกครั้ง</p>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="list-group">
        {savedRecordings.map((recording) => {
          const onServer = recording.session?.received_chunks?.length || 0;
          const totalSlices = recording.sliceSizes?.length;

          return (
            <div key={recording.id} className="list-group-item">
              <div className="d-flex w-100 justify-content-between align-items-start">
                <div>
                  <strong>{new Date(recording.timestamp).toLocaleString('th-TH')}</strong>
                  <div className="small text-muted">
                    ขนาด: {(recording.blob.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                  {onServer > 0 && (
                    <div className="small text-info">
                      อัพโหลดไปแล้ว: {onServer}{totalSlices ? `/${totalSlices}` : ''} ส่วน
                    </div>
                  )}
                </div>
                <div className="d-flex flex-column gap-1">
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleRetryUpload(recording)}
                    disabled={!!uploadingId}
                  >
                    {uploadingId === recording.id
                      ? <><LoadingSpinner small /> กำลังอัพโหลด...</>
                      : onServer > 0
                        ? 'อัพโหลดต่อ'
                        : 'ลองอัพโหลดอีกครั้ง'}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(recording)}
                    disabled={!!uploadingId}
                  >
                    ลบ
                  </button>
                </div>
              </div>
              {uploadingId === recording.id && uploadProgress > 0 && (
                <div className="mt-2">
                  <div className="progress" style={{ height: '15px' }}>
                    <div
                      className={`progress-bar ${uploadProgress >= 100 ? 'bg-success' : 'progress-bar-striped progress-bar-animated'}`}
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                  <small className="text-muted">
                    {uploadProgress >= 90 ? 'กำลังประมวลผลบนเซิร์ฟเวอร์...' : 'กำลังอัพโหลด...'}
                  </small>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SavedRecordings;
